import React, { useState, useEffect, useRef, useCallback } from "react";
import jsQR from "jsqr";
import nacl from "tweetnacl";
import api from "../api/homeapi";
import { CameraIcon, CheckIcon, XIcon, AlertIcon, WifiIcon, WifiOffIcon } from "../components/Icons";
import { StaffFrame } from "./DashboardShared";

const C = { green:"#006B3C", greenDk:"#004D2B", greenLt:"#E8F5EE", yellow:"#FFC300", yellowLt:"#FFF8DC", grayBd:"#E0E0E0", gray:"#F5F5F5", text:"#1A1A1A", textSub:"#666" };

const LS_PUBLIC_KEY = "ctrl_public_key";
const LS_MANIFEST_PREFIX = "ctrl_manifest_";
const LS_QUEUE = "ctrl_offline_queue";

/* ═══════════════════════════════════════
   Utilitaires stockage local (cache hors-ligne)
═══════════════════════════════════════ */
const loadJSON = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const saveJSON = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

/* ═══════════════════════════════════════
   Vérification de signature (Ed25519, 100% côté client)
   Reconstruit exactement l'ordre de champs utilisé par
   TicketSigningService::buildCanonicalPayload côté back.
═══════════════════════════════════════ */
const CANONICAL_ORDER = ["ref","nom","siege","depart","arrivee","date","heure","train","classe","prix","type","bookingId","tripId"];

function canonicalize(payload) {
  const ordered = {};
  for (const k of CANONICAL_ORDER) ordered[k] = payload[k] ?? "";
  return JSON.stringify(ordered);
}

function base64ToBytes(b64) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function verifySignature(payload, signatureB64, publicKeyB64) {
  if (!signatureB64 || !publicKeyB64) return "UNVERIFIABLE";
  try {
    const message = new TextEncoder().encode(canonicalize(payload));
    const signature = base64ToBytes(signatureB64);
    const publicKey = base64ToBytes(publicKeyB64);
    return nacl.sign.detached.verify(message, signature, publicKey) ? "VALID" : "INVALID";
  } catch {
    return "UNVERIFIABLE";
  }
}

/* ═══════════════════════════════════════
   Scanner caméra (jsQR, aucune dépendance réseau)
═══════════════════════════════════════ */
const CameraScanner = ({ onDetect, active }) => {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef    = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "environment" } })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        tick();
      })
      .catch(() => setError("Caméra indisponible - utilisez la saisie manuelle ci-dessous."));

    const tick = () => {
      const video = videoRef.current, canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) {
          onDetect(code.data);
          return; // on laisse le parent décider de relancer le scan
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [active, onDetect]);

  if (!active) return null;

  return (
    <div style={{ position:"relative", borderRadius:14, overflow:"hidden", background:"#000", maxWidth:360, margin:"0 auto" }}>
      <video ref={videoRef} muted playsInline style={{ width:"100%", display:"block" }} />
      <canvas ref={canvasRef} style={{ display:"none" }} />
      <div style={{ position:"absolute", inset:24, border:`3px solid ${C.yellow}`, borderRadius:12, pointerEvents:"none" }} />
      {error && (
        <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.8)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", padding:20, textAlign:"center", fontSize:13 }}>
          {error}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════
   PAGE PRINCIPALE
═══════════════════════════════════════ */
export default function ControllerScan() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [trips, setTrips]       = useState([]);
  const [tripId, setTripId]     = useState("");
  const [manifest, setManifest] = useState(null);
  const [publicKey, setPublicKey] = useState(loadJSON(LS_PUBLIC_KEY, null));
  const [queue, setQueue]       = useState(loadJSON(LS_QUEUE, []));
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const [result, setResult]     = useState(null); // { status, ticket, sigStatus, alreadyBoarded }
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  /* Connectivité */
  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  /* Clé publique - tentative de rafraîchissement si en ligne, sinon on garde le cache */
  useEffect(() => {
    if (!isOnline) return;
    api.get("/tickets/public-key").then(r => {
      if (r.data?.publicKey) {
        setPublicKey(r.data.publicKey);
        saveJSON(LS_PUBLIC_KEY, r.data.publicKey);
      }
    }).catch(() => {});
  }, [isOnline]);

  /* Liste des trains (pour choisir lequel contrôler) */
  useEffect(() => {
    if (!isOnline) return;
    api.get("/api/trips").then(r => setTrips(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [isOnline]);

  /* Synchronisation de la file d'attente hors-ligne dès que la connexion revient */
  const syncQueue = useCallback(async () => {
    const q = loadJSON(LS_QUEUE, []);
    if (!isOnline || q.length === 0) return;
    try {
      const res = await api.post("/api/admin/bookings/board-batch", { checkins: q });
      const results = res.data?.results || [];
      const failed = q.filter(item => {
        const r = results.find(x => x.bookingId === item.bookingId);
        return r && r.status === "NOT_FOUND";
      });
      saveJSON(LS_QUEUE, failed);
      setQueue(failed);
      if (q.length > failed.length) showToast(`${q.length - failed.length} contrôle(s) synchronisé(s) ✓`);
    } catch {
      // toujours hors-ligne ou serveur injoignable - on retentera plus tard
    }
  }, [isOnline]);

  useEffect(() => { syncQueue(); }, [isOnline, syncQueue]);

  /* Chargement (et mise en cache) du manifeste d'un train */
  const loadManifest = async (id) => {
    if (!id) return;
    setLoadingManifest(true);
    try {
      if (isOnline) {
        const r = await api.get(`/api/admin/trips/${id}/manifest`);
        setManifest(r.data);
        saveJSON(LS_MANIFEST_PREFIX + id, r.data);
      } else {
        const cached = loadJSON(LS_MANIFEST_PREFIX + id, null);
        if (cached) { setManifest(cached); showToast("Mode hors-ligne : manifeste depuis le cache local"); }
        else showToast("Aucun manifeste en cache pour ce train - connectez-vous une fois pour le télécharger.");
      }
    } catch {
      const cached = loadJSON(LS_MANIFEST_PREFIX + id, null);
      if (cached) { setManifest(cached); showToast("Serveur injoignable : manifeste depuis le cache local"); }
    } finally {
      setLoadingManifest(false);
    }
  };

  useEffect(() => { if (tripId) loadManifest(tripId); }, [tripId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Traitement d'un contenu scanné ou saisi manuellement - vérifie
     directement (en ligne : recherche par référence ; hors-ligne : dans
     TOUS les manifestes déjà téléchargés), sans obliger à choisir un train. */
  const handleScanned = useCallback(async (raw) => {
    setScanning(false);
    let payload = null;
    try { payload = JSON.parse(raw); } catch { payload = null; }

    // QR scanné : payload JSON signé - vérification cryptographique directe.
    if (payload) {
      const sigStatus = verifySignature(payload, payload.sig, publicKey);
      const inManifest = manifest?.tickets?.find(t => t.bookingId === payload.bookingId || t.reference === payload.ref);
      setResult({
        status: "SCANNED", payload, sigStatus,
        ticket: inManifest || null,
        alreadyBoarded: !!(inManifest?.boardedAt) || queue.some(q => q.bookingId === (inManifest?.bookingId ?? payload.bookingId)),
      });
      return;
    }

    // Référence tapée/scannée (texte simple, ex: SETRAG-A3F7K2)
    const ref = raw.trim();

    if (isOnline) {
      try {
        const r = await api.get(`/api/admin/bookings/lookup/${encodeURIComponent(ref)}`);
        const d = r.data;
        setResult({
          status: "SCANNED",
          payload: d.payload,
          sigStatus: verifySignature(d.payload, d.signature, publicKey),
          ticket: { bookingId: d.bookingId, reference: d.reference, passenger: d.passenger, seat: d.seat, boardedAt: d.boardedAt },
          alreadyBoarded: !!d.boardedAt || queue.some(q => q.bookingId === d.bookingId),
          tripInfo: d.trip,
        });
      } catch {
        setResult({ status: "NOT_FOUND", raw });
      }
      return;
    }

    // Hors-ligne : on cherche dans TOUS les manifestes déjà téléchargés,
    // pas seulement celui du train actuellement sélectionné.
    let found = null, foundTripInfo = null;
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(LS_MANIFEST_PREFIX)) continue;
      const m = loadJSON(key, null);
      const t = m?.tickets?.find(x => x.reference === ref);
      if (t) { found = t; foundTripInfo = m.trip; break; }
    }
    if (found) {
      setResult({
        status: "FOUND_BY_REFERENCE", ticket: found, sigStatus: "UNVERIFIABLE",
        alreadyBoarded: !!found.boardedAt || queue.some(q => q.bookingId === found.bookingId),
        tripInfo: foundTripInfo,
      });
    } else {
      setResult({ status: "NOT_FOUND", raw, offlineNoManifest: true });
    }
  }, [manifest, publicKey, queue, isOnline]);

  /* Validation de l'embarquement - en ligne immédiat, sinon file d'attente locale */
  const confirmBoarding = async () => {
    const bookingId = result?.ticket?.bookingId ?? result?.payload?.bookingId;
    if (!bookingId) return;

    const nowIso = new Date().toISOString();

    if (isOnline) {
      try {
        await api.post(`/api/admin/bookings/${bookingId}/board`, { boardedAt: nowIso });
        showToast("Embarquement validé ✓");
      } catch {
        showToast("Échec de l'enregistrement - mis en file d'attente hors-ligne.");
        enqueueOffline(bookingId, nowIso);
      }
    } else {
      enqueueOffline(bookingId, nowIso);
      showToast("Hors-ligne : embarquement mis en file, sera synchronisé automatiquement.");
    }

    // Mise à jour optimiste du manifeste en cache pour éviter un double-scan
    setManifest(prev => {
      if (!prev) return prev;
      const updated = { ...prev, tickets: prev.tickets.map(t => t.bookingId === bookingId ? { ...t, boardedAt: nowIso } : t) };
      saveJSON(LS_MANIFEST_PREFIX + tripId, updated);
      return updated;
    });
    setResult(null);
  };

  const enqueueOffline = (bookingId, boardedAt) => {
    const q = loadJSON(LS_QUEUE, []);
    q.push({ bookingId, boardedAt, boardedBy: "Contrôleur" });
    saveJSON(LS_QUEUE, q);
    setQueue(q);
  };

  const tripMeta = result?.tripInfo || manifest?.trip || null;
  const ticketDisplay = result?.ticket
    ? {
        reference: result.ticket.reference,
        passenger: result.ticket.passenger,
        seat: result.ticket.seat,
        train: tripMeta?.trainNumber || result?.payload?.train,
        route: tripMeta ? `${tripMeta.departure} → ${tripMeta.arrival}` : (result?.payload ? `${result.payload.depart} → ${result.payload.arrivee}` : ""),
        date: tripMeta?.date || result?.payload?.date,
        boardedAt: result.ticket.boardedAt,
      }
    : result?.payload
    ? {
        reference: result.payload.ref,
        passenger: result.payload.nom,
        seat: result.payload.siege,
        train: result.payload.train,
        route: `${result.payload.depart} → ${result.payload.arrivee}`,
        date: result.payload.date,
        boardedAt: null,
      }
    : null;

  return (
    <StaffFrame>
      <div style={{ padding:28, maxWidth:640, margin:"0 auto" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <div>
            <h1 style={{ margin:0, fontSize:22, fontWeight:800 }}>Contrôle des billets</h1>
            <p style={{ margin:0, color:C.textSub, fontSize:13 }}>Scannez ou saisissez un billet pour vérifier son authenticité et valider l'embarquement.</p>
          </div>
        </div>

        {/* Scanner / saisie - action principale, aucune sélection de train requise */}
        {!result && (
          <>
            {scanning ? (
              <>
                <CameraScanner active={scanning} onDetect={handleScanned} />
                <button onClick={() => setScanning(false)}
                  style={{ display:"block", margin:"14px auto 0", background:"#fff", border:`1.5px solid ${C.grayBd}`, borderRadius:10, padding:"8px 20px", fontWeight:600, cursor:"pointer" }}>
                  Annuler le scan
                </button>
              </>
            ) : (
              <button onClick={() => setScanning(true)}
                style={{ width:"100%", background:C.green, color:"#fff", border:"none", borderRadius:12, padding:"14px", fontWeight:700, fontSize:15, cursor:"pointer", marginBottom:14 }}>
                <CameraIcon size={16}/> &nbsp;Scanner un QR code
              </button>
            )}

            <div style={{ display:"flex", gap:8 }}>
              <input
                placeholder="Ou saisir le numéro du billet (ex: SETRAG-A3F7K2)"
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && manualInput.trim() && handleScanned(manualInput.trim())}
                style={{ flex:1, border:`1.5px solid ${C.grayBd}`, borderRadius:10, padding:"11px 14px", fontSize:14 }}
              />
              <button onClick={() => manualInput.trim() && handleScanned(manualInput.trim())}
                style={{ background:C.greenDk, color:"#fff", border:"none", borderRadius:10, padding:"0 18px", fontWeight:700, cursor:"pointer" }}>
                Vérifier
              </button>
            </div>

            {!isOnline && (
              <p style={{ fontSize:12, color:"#B45309", marginTop:10 }}>
                Hors-ligne : la vérification se fait sur les manifestes déjà téléchargés (voir « Préparer le contrôle hors-ligne » ci-dessous).
              </p>
            )}
          </>
        )}

        {/* Préparation hors-ligne - optionnelle, pour pouvoir contrôler sans réseau pendant le trajet */}
        <details style={{ marginTop:24, marginBottom:10 }}>
          <summary style={{ cursor:"pointer", fontSize:13, fontWeight:700, color:C.textSub }}>
            Préparer le contrôle hors-ligne (optionnel)
          </summary>
          <div style={{ marginTop:12 }}>
            <label style={{ display:"block", fontSize:12, fontWeight:700, color:C.textSub, marginBottom:6, textTransform:"uppercase" }}>Précharger un train</label>
            <select value={tripId} onChange={e => setTripId(e.target.value)}
              style={{ width:"100%", border:`1.5px solid ${C.grayBd}`, borderRadius:10, padding:"10px 12px", fontSize:14 }}>
              <option value="">- Choisir un train -</option>
              {trips.map(t => (
                <option key={t.id} value={t.id}>
                  {t.trainNumber} · {t.departure} → {t.arrival} · {t.date} {t.departureTime}
                </option>
              ))}
            </select>
            {loadingManifest && <p style={{ fontSize:12, color:C.textSub, marginTop:6 }}>Chargement du manifeste…</p>}
            {manifest && (
              <p style={{ fontSize:12, color:C.green, marginTop:6 }}>
                ✓ Manifeste chargé - {manifest.tickets.length} billet(s), disponible hors-ligne pour ce train.
              </p>
            )}
          </div>
        </details>

        {!publicKey && (
          <p style={{ fontSize:12, color:"#B45309", marginBottom:16 }}>
            Clé de vérification non configurée côté serveur (voir TICKET_SIGNING_*_KEY dans .env) - les signatures ne pourront pas être vérifiées cryptographiquement, seules les données du billet feront foi.
          </p>
        )}

        {/* Statut connexion + file d'attente */}
        <div style={{
          display:"flex", alignItems:"center", gap:10, marginBottom:20, padding:"10px 14px",
          borderRadius:10, background: isOnline ? C.greenLt : "#FEF3C7", border:`1px solid ${isOnline ? C.green : "#F59E0B"}`,
        }}>
          <span style={{ display:"flex", alignItems:"center", gap:7, fontSize:13, fontWeight:600, color: isOnline ? C.greenDk : "#92400E" }}>
            {isOnline ? <><WifiIcon size={15}/> En ligne</> : <><WifiOffIcon size={15}/> Hors-ligne - vérification locale active</>}
          </span>
          {queue.length > 0 && (
            <span style={{ fontSize:12, color:"#92400E", marginLeft:"auto" }}>
              {queue.length} contrôle(s) en attente de synchronisation
            </span>
          )}
        </div>

        {/* Résultat du scan */}
        {result && (
          <div style={{ background:"#fff", border:`2px solid ${C.grayBd}`, borderRadius:16, padding:20, marginTop:10 }}>
            {result.status === "NOT_FOUND" ? (
              <div style={{ textAlign:"center", padding:20 }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:10, color:"#B91C1C" }}><XIcon size={34}/></div>
                <p style={{ fontWeight:700, color:"#B91C1C" }}>Billet introuvable</p>
                <p style={{ fontSize:13, color:C.textSub }}>Ni le manifeste local ni le QR scanné ne correspondent à ce code.</p>
              </div>
            ) : (
              <>
                <div style={{ textAlign:"center", marginBottom:16 }}>
                  {result.sigStatus === "VALID" && <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:C.green, fontWeight:800, fontSize:16 }}><CheckIcon size={19}/> Signature valide</div>}
                  {result.sigStatus === "INVALID" && <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:"#B91C1C", fontWeight:800, fontSize:16 }}><XIcon size={19}/> Signature invalide - billet potentiellement falsifié</div>}
                  {result.sigStatus === "UNVERIFIABLE" && <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:"#B45309", fontWeight:800, fontSize:16 }}><AlertIcon size={19}/> Signature non vérifiable (clé indisponible)</div>}
                  {result.alreadyBoarded && (
                    <div style={{ marginTop:8, background:"#FEF3C7", color:"#92400E", borderRadius:8, padding:"8px 12px", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:7, justifyContent:"center" }}>
                      <AlertIcon size={14}/> Ce billet a déjà été contrôlé {ticketDisplay?.boardedAt ? `(${new Date(ticketDisplay.boardedAt).toLocaleString("fr-FR")})` : ""}
                    </div>
                  )}
                </div>

                {ticketDisplay && (
                  <div style={{ background:C.gray, borderRadius:12, padding:16, display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 16px", fontSize:13, marginBottom:16 }}>
                    <div style={{ gridColumn:"1/-1" }}>
                      <div style={{ fontSize:10, color:C.textSub, letterSpacing:1 }}>PASSAGER</div>
                      <div style={{ fontWeight:700, fontSize:16 }}>{ticketDisplay.passenger}</div>
                    </div>
                    <div><div style={{ fontSize:10, color:C.textSub }}>RÉFÉRENCE</div><div style={{ fontWeight:600, fontFamily:"monospace" }}>{ticketDisplay.reference}</div></div>
                    <div><div style={{ fontSize:10, color:C.textSub }}>SIÈGE</div><div style={{ fontWeight:700, color:C.green }}>N°{ticketDisplay.seat}</div></div>
                    <div><div style={{ fontSize:10, color:C.textSub }}>TRAJET</div><div style={{ fontWeight:600 }}>{ticketDisplay.route}</div></div>
                    <div><div style={{ fontSize:10, color:C.textSub }}>TRAIN / DATE</div><div style={{ fontWeight:600 }}>{ticketDisplay.train} · {ticketDisplay.date}</div></div>
                  </div>
                )}

                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={() => setResult(null)}
                    style={{ flex:1, background:"#fff", border:`1.5px solid ${C.grayBd}`, borderRadius:10, padding:"12px", fontWeight:600, cursor:"pointer" }}>
                    Scanner suivant
                  </button>
                  {!result.alreadyBoarded && (result.ticket?.bookingId || result.payload?.bookingId) && (
                    <button onClick={confirmBoarding}
                      style={{ flex:2, background:C.green, color:"#fff", border:"none", borderRadius:10, padding:"12px", fontWeight:700, cursor:"pointer" }}>
                      ✓ Valider l'embarquement
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {toast && (
          <div style={{ position:"fixed", bottom:20, left:"50%", transform:"translateX(-50%)", background:C.greenDk, color:"#fff", padding:"10px 20px", borderRadius:10, fontSize:13, boxShadow:"0 8px 24px rgba(0,0,0,0.2)" }}>
            {toast}
          </div>
        )}
      </div>
    </StaffFrame>
  );
}
